import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { AlertOctagon, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";

/** Maps assessment severity → maintenance request priority */
const SEVERITY_TO_PRIORITY = { Severe: "urgent", Medium: "high", Low: "medium" };

const blank = {
  any_broken_currently: "",          // 1: Yes/No
  severity: "",                      // 2: Severe/Medium/Low
  noticed_at: "",                    // 3: datetime-local
  property_has_broken: "",           // 4: Yes/No (gate for 4.0+)
  total_require_repair: "",          // 4.0
  total_require_replacement: "",     // 4.1
  tagged_and_removed: "",            // 4.1.0 — shown when 4.1 > 1
  description_of_damage: "",         // 4.2
  contacted_insurance: "",           // 4.3 — Yes/No
  insurance_details: "",             // 4.3.0 — shown when 4.3 = Yes
  insurance_followup: "",            // 4.4 — shown when 4.3 = No (required action)
  photo_paths: [],                   // attached photos (file IDs)
};

function YesNoGroup({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {["Yes", "No"].map(v => (
        <button
          key={v}
          type="button"
          data-testid={`${testid}-${v.toLowerCase()}`}
          onClick={() => onChange(v)}
          className={`px-4 py-1.5 rounded-md border text-sm font-medium transition-all ${value === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"}`}
        >{v}</button>
      ))}
    </div>
  );
}

function Question({ num, text, required, children, gate }) {
  if (gate === false) return null;
  return (
    <div className="space-y-2 py-3 border-b border-slate-100 last:border-b-0">
      <Label className="text-sm text-slate-800 leading-snug flex gap-1.5">
        <span className="font-mono-num text-slate-400 shrink-0">{num}</span>
        <span>{text}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      </Label>
      <div className="pl-7">{children}</div>
    </div>
  );
}

export default function ApplianceAssessmentDialog({ open, onOpenChange, properties, onCreated }) {
  const [propertyId, setPropertyId] = useState("");
  const [a, setA] = useState(blank);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setA(prev => ({ ...prev, [k]: v }));

  const reset = () => { setA(blank); setPropertyId(""); };

  // Conditional gates
  const requireRepairReplaceBlock = a.property_has_broken === "Yes";
  const showTaggedRemoved = Number(a.total_require_replacement) > 1;
  const showInsuranceDetails = a.contacted_insurance === "Yes";
  const showInsuranceFollowup = a.contacted_insurance === "No";

  const submit = async (e) => {
    e.preventDefault();
    if (!propertyId) { toast.error("Please select a property"); return; }
    if (!a.any_broken_currently || !a.severity) { toast.error("Please answer all required questions"); return; }
    setSubmitting(true);
    try {
      const priority = SEVERITY_TO_PRIORITY[a.severity] || "medium";
      const title = "Appliance Damage Assessment";
      const desc = a.description_of_damage ||
        `Severity: ${a.severity}. ${a.any_broken_currently === "Yes" ? "Appliance(s) currently broken." : "No active damage at submission."}`;
      await api.post("/maintenance", {
        property_id: propertyId,
        title,
        description: desc,
        priority,
        assessment_type: "appliance",
        assessment: a,
        photo_paths: a.photo_paths || [],
      });
      toast.success("Assessment submitted");
      onOpenChange(false);
      reset();
      onCreated?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appliance-assessment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-slate-700" />
            Appliances — Broken or Damaged Assessment
          </DialogTitle>
        </DialogHeader>

        <Card className="bg-amber-50 border-amber-200 p-3 mb-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertOctagon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>Please answer all required questions. Some follow-up questions appear automatically based on your answers.</span>
        </Card>

        <form onSubmit={submit} className="space-y-2">
          <div className="space-y-1.5 pb-2 border-b border-slate-100">
            <Label className="text-xs">Property *</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger data-testid="aa-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <Question num="1" text="Broken Appliances" required>
            <YesNoGroup testid="aa-q1" value={a.any_broken_currently} onChange={(v) => set("any_broken_currently", v)} />
          </Question>

          <Question num="2" text="Tenant categorization of severity" required>
            <Select value={a.severity} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger data-testid="aa-severity"><SelectValue placeholder="Select severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Severe">Severe</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </Question>

          <Question num="3" text="Date & time tenant noticed the appliance was broken/damaged">
            <Input data-testid="aa-noticed-at" type="datetime-local" value={a.noticed_at} onChange={(e) => set("noticed_at", e.target.value)} />
          </Question>

          <Question num="4" text="Are there any appliances broken or damaged currently in the rental property?" required>
            <YesNoGroup testid="aa-q4" value={a.property_has_broken} onChange={(v) => set("property_has_broken", v)} />
            {a.property_has_broken === "Yes" && (
              <p className="text-xs text-amber-700 mt-1.5 italic">⚠ Action required — please answer questions 4.0 through 4.4 below.</p>
            )}
          </Question>

          <Question num="4.0" text="How many appliances in total require repair?" gate={requireRepairReplaceBlock}>
            <Input data-testid="aa-repair-count" type="number" min="0" value={a.total_require_repair} onChange={(e) => set("total_require_repair", e.target.value)} className="w-32" />
          </Question>

          <Question num="4.1" text="How many appliances in total require replacement?" gate={requireRepairReplaceBlock}>
            <Input data-testid="aa-replace-count" type="number" min="0" value={a.total_require_replacement} onChange={(e) => set("total_require_replacement", e.target.value)} className="w-32" />
          </Question>

          <Question num="4.1.0" text="Have all broken appliances been tagged and removed from the property?" gate={requireRepairReplaceBlock && showTaggedRemoved}>
            <YesNoGroup testid="aa-q410" value={a.tagged_and_removed} onChange={(v) => set("tagged_and_removed", v)} />
          </Question>

          <Question num="4.2" text="Please describe which appliances are broken or damaged and the nature of the malfunction." gate={requireRepairReplaceBlock}>
            <Textarea data-testid="aa-description" rows={3} value={a.description_of_damage} onChange={(e) => set("description_of_damage", e.target.value)} placeholder="e.g. Refrigerator not cooling; washing machine drum loose…" />
          </Question>

          <Question num="4.3" text="Did the tenant contact the Rental Insurance Company?" gate={requireRepairReplaceBlock}>
            <YesNoGroup testid="aa-q43" value={a.contacted_insurance} onChange={(v) => set("contacted_insurance", v)} />
          </Question>

          <Question num="4.3.0" text="Please provide the name of the representative, claim number and total amount the insurance will pay. If not covered, the tenant may be charged for the request — the landlord will provide the cost in writing before any charges." gate={requireRepairReplaceBlock && showInsuranceDetails}>
            <Textarea data-testid="aa-insurance-details" rows={3} value={a.insurance_details} onChange={(e) => set("insurance_details", e.target.value)} placeholder="Rep name · Claim # · Amount covered…" />
          </Question>

          <Question num="4.4" text="Please contact the Rental Insurance Company and proceed with a claim. Write down the rep name, claim number, date/time of the call, and the response from the rep. This information is required to proceed with any decisions." gate={requireRepairReplaceBlock && showInsuranceFollowup}>
            <Textarea data-testid="aa-insurance-followup" rows={3} value={a.insurance_followup} onChange={(e) => set("insurance_followup", e.target.value)} placeholder="Rep name · Claim # · Date/time · Response…" />
          </Question>

          <Question num="5" text="Photos of the damage (optional but recommended)">
            <PhotoUploader value={a.photo_paths} onChange={(v) => set("photo_paths", v)} testid="aa-photo" />
          </Question>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="aa-submit" type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
              {submitting ? "Submitting…" : "Submit Assessment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
